import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LevelResult } from '@dave/level-contract';
import { loadAssetsCatalog } from '@/api/assetsCatalog';
import type { LevelConfigDto } from '@/api/dto';
import { routes } from '@/api/routes';
import { loadManifest } from '@/meta/useManifest';
import { authorizedFetch, authStore, useAuth } from '@/auth/authStore';
import { getPrefs, usePrefs } from '@/meta/prefsStore';
import { storyMarkersForLevel, storyVideoUrl, type LevelStoryMarkers } from '@/meta/story';
import { StoryCinematic } from '@/meta/StoryPlayer';
import { flushPendingResults, loadProgressMap, postLevelResult } from '@/save/progress';
import { applyResult, gainFor } from '@/save/saveStore';
import { playProgressSfx } from '@/meta/metaSfx';
import { isStoryFailed, isStoryWatched, markStoryFailed, markStoryWatched } from '@/save/storyStore';
import { levelStates, nextLevelId } from '@/save/unlock';
import { LevelResultPanel } from '@/screens/LevelResultPanel';
import { PausePanel } from '@/screens/PausePanel';
import { useLevelHost } from '@/screens/useLevelHost';

/** Сейв всегда локально (кэш/гость); залогиненным — ещё и POST на сервер (§4.2 alt-ветка)
 *  через очередь dg_pending_v1 (фейл сети не теряет результат). Возвращает промису
 *  отправки — гейт следующего уровня ждёт её, иначе сервер ответит level_locked. */
function persistResult(levelId: string, r: LevelResult): Promise<void> {
  applyResult(levelId, {
    rating: r.rating, status: r.status, durationMs: r.durationMs,
    objectives: r.objectives,
  });
  if (authStore.get().status !== 'authed' || !authStore.accessToken) return Promise.resolve();
  return postLevelResult(levelId, {
    status: r.status, rating: r.rating, maxRating: r.maxRating,
    objectives: r.objectives, metrics: r.metrics, durationMs: r.durationMs,
  });
}

interface LevelGate { unlocked: boolean; nextId: string | null; story: LevelStoryMarkers }

/** Ролик очереди: эпизод + его URL (S3-ключ). */
interface StoryClip { ep: string; url: string }

/** Guard «unlocked» (§4.1/§9) + следующий уровень для «Далее» на победе
 *  + сюжет-маркеры уровня (docs/story-videos.md).
 *  Манифест недоступен или уровень не из каталога → fail-open (nextId null,
 *  кнопка просто не покажется), гейт — UX-правило, не защита контента. */
async function loadGate(levelId: string): Promise<LevelGate> {
  try {
    const [m, progress] = await Promise.all([
      loadManifest(), // общий промис-кэш манифеста (Ф5) — один fetch на сессию
      loadProgressMap(),
    ]);
    if (!m.levels.some((l) => l.id === levelId)) {
      return { unlocked: true, nextId: null, story: { before: [], after: [] } };
    }
    return {
      unlocked: levelStates(m.worlds, m.levels, progress)[levelId] !== 'locked',
      nextId: nextLevelId(m.worlds, m.levels, levelId),
      story: storyMarkersForLevel(m.levels, levelId),
    };
  } catch {
    return { unlocked: true, nextId: null, story: { before: [], after: [] } };
  }
}

export function PlayScreen() {
  const { levelId } = useParams<{ levelId: string }>();
  const [nextId, setNextId] = useState<string | null>(null); // «далее» на победе
  const [paywallLogin, setPaywallLogin] = useState(false); // paywall от 401: зовём войти
  // сюжет-ролики (docs/story-videos.md): очередь показа — before до загрузки
  // плеера (next='level'; у первого уровня Мира 1 это [пролог, интро рампы]),
  // after — на победе до окна результата (next='result')
  const [storyQueue, setStoryQueue] =
    useState<{ clips: StoryClip[]; next: 'level' | 'result' } | null>(null);
  const afterClipsRef = useRef<StoryClip[]>([]);
  const auth = useAuth();
  const resultPostRef = useRef<Promise<void> | null>(null); // POST последнего результата
  // отмена отложенных наградных звуков: без неё разблокировка догонит игрока уже на карте,
  // если он закроет окно результата быстрее, чем через 1.8 с
  const cancelProgressSfxRef = useRef<(() => void) | null>(null);
  useEffect(() => () => cancelProgressSfxRef.current?.(), []);
  const prefs = usePrefs();
  const navigate = useNavigate();
  // пауза (кит §04): шестерёнка в HUD → громкости + «Продолжить/Заново/Выйти»
  const [paused, setPaused] = useState(false);

  const host = useLevelHost<'locked' | 'paywall' | 'story'>({
    // guard считается по прогрессу (сервер/локальный сейв) — ждём, пока bootstrap
    // определит, кто мы; иначе залогиненного оценим по гостевому блобу
    enabled: auth.status !== 'loading' && !!levelId,
    key: `${levelId}·${auth.status}`,
    // громкости из экрана настроек (dg_prefs_v1) — свежие на момент сборки конфига
    getVolumes: () => {
      const p = getPrefs();
      return { music: p.music, sfx: p.sfx };
    },
    async loadConfig(ctl) {
      // экранное состояние прошлого уровня — в ноль (скелет чистит только своё)
      setNextId(null);
      setStoryQueue(null);
      afterClipsRef.current = [];
      // не читаем прогресс, пока POST результата не доехал (или ~3с) — иначе
      // сервер ответит level_locked на только что открытый «Далее»-уровень;
      // заодно флашим висящие результаты из dg_pending_v1
      await Promise.race([
        Promise.resolve(resultPostRef.current).then(() => flushPendingResults()),
        new Promise((r) => setTimeout(r, 3000)),
      ]);
      if (ctl.cancelled()) return null;
      const gate = await loadGate(levelId!);
      if (ctl.cancelled()) return null;
      setNextId(gate.nextId);
      if (!gate.unlocked) {
        ctl.setPhase('locked');
        return null;
      }
      // каталог ассетов нужен и роликам, и медиа уровня — тянем ОДИН раз
      // (модульный кэш — скелет хоста возьмёт его же). Недоступен → ролики
      // пропускаем целиком, симметрично before/after (иначе транзиентный сбой
      // на before мог бы дать before-без-after или наоборот); уровень всё равно
      // пробуем — media-блок скелета даст свою ошибку, если медиа реально нет.
      let catalog: Awaited<ReturnType<typeof loadAssetsCatalog>> | null = null;
      try { catalog = await loadAssetsCatalog(); } catch { /* ролики пропустим */ }
      if (ctl.cancelled()) return null;

      // сюжет-ролики перед уровнем — каждый один раз (dg_story_v1)
      const beforeEps = catalog
        ? gate.story.before.filter((ep) => !isStoryWatched(ep) && !isStoryFailed(ep))
        : [];
      if (catalog && beforeEps.length) {
        setStoryQueue({
          clips: beforeEps.map((ep) => ({ ep, url: storyVideoUrl(catalog!.mediaBaseUrl, ep) })),
          next: 'level',
        });
        ctl.setPhase('story');
        return null; // finishStory перезапустит эффект и загрузит уровень
      }
      // единый путь §4.2: /config отдаёт и free; paid — только с Bearer + покупкой;
      // Bearer и авто-refresh протухшего токена — внутри authorizedFetch
      const resp = await authorizedFetch(routes.levelConfig(levelId!), {
        credentials: 'include',
      });
      if (!resp.ok) {
        // серверный гейт: конверт ApiError {error:{code}}; битое тело → generic
        let code = '';
        try {
          code = ((await resp.json()) as { error?: { code?: string } }).error?.code ?? '';
        } catch { /* не-JSON — упадём в generic ниже */ }
        if (ctl.cancelled()) return null;
        if (code === 'level_locked') { ctl.setPhase('locked'); return null; }
        if (code === 'payment_required') { setPaywallLogin(false); ctl.setPhase('paywall'); return null; }
        if (resp.status === 401 && (code === 'session_unauthenticated' || code === 'UNAUTHENTICATED')) {
          setPaywallLogin(true); ctl.setPhase('paywall'); return null;
        }
        throw new Error(`config ${resp.status}`);
      }
      // ролики после первой победы готовим заранее: onComplete синхронный
      if (catalog) {
        const mediaRoot = catalog.mediaBaseUrl;
        afterClipsRef.current = gate.story.after
          .filter((ep) => !isStoryWatched(ep) && !isStoryFailed(ep))
          .map((ep) => ({ ep, url: storyVideoUrl(mediaRoot, ep) }));
      }
      return (await resp.json()) as LevelConfigDto;
    },
    onComplete(r) {
      // ЗВУК ДО СЕЙВА: `gainFor` сравнивает результат с прежним рекордом, а `applyResult`
      // этот рекорд перезаписывает — после мёржа «впервые» уже неотличимо от «снова»
      const gain = gainFor(levelId!, r);
      resultPostRef.current = persistResult(levelId!, r);
      cancelProgressSfxRef.current?.();
      cancelProgressSfxRef.current = playProgressSfx(gain);
      // after-ролики — только на первой победе (иначе ref уже пуст/watched)
      const afterClips = r.status === 'completed' ? afterClipsRef.current : [];
      if (afterClips.length) {
        afterClipsRef.current = [];
        setStoryQueue({ clips: afterClips, next: 'result' });
        return 'story';
      }
      return 'result';
    },
  });

  /** Текущий ролик очереди доиграл/пропущен (watched) или не загрузился (failed —
   *  в этой сессии больше не пытаемся, иначе цикл; файл дольют — покажется после
   *  перезагрузки). Дальше — следующий ролик очереди, либо уровень (через
   *  перезапуск эффекта) / окно результата. */
  const finishStory = (watched: boolean) => {
    if (!storyQueue) return;
    const [head, ...rest] = storyQueue.clips;
    if (head) {
      if (watched) markStoryWatched(head.ep);
      else markStoryFailed(head.ep);
    }
    if (rest.length) {
      setStoryQueue({ clips: rest, next: storyQueue.next });
      return;
    }
    setStoryQueue(null);
    if (storyQueue.next === 'result') {
      host.setPhase('result');
    } else {
      // на 'level' фаза остаётся 'story', пока эффект не выставит 'loading' —
      // мостим индикатором, иначе один кадр между роликом и уровнем чёрный
      host.setPhase('loading');
      host.restart();
    }
  };

  // громкости применяются к идущему уровню живьём (контракт 1.1, setVolumes
  // опционален); стартовые значения плеер берёт из LevelConfig.settings
  useEffect(() => {
    host.instanceRef.current?.setVolumes?.(prefs.music, prefs.sfx);
  }, [prefs.music, prefs.sfx]);

  const { phase, result, error } = host;

  // мир плеера стоит, пока открыта пауза: только в фазе 'playing' (в загрузке/ролике
  // инстанса ещё нет). Новый инстанс («Заново», «Далее») рождается без паузы — флаг
  // снимается вместе со сменой фазы, иначе панель повисла бы над свежим уровнем.
  useEffect(() => {
    if (!paused || phase !== 'playing') return;
    const inst = host.instanceRef.current;
    inst?.pause();
    // «Заново» успевает уничтожить инстанс раньше этой очистки (эффект хоста объявлен
    // выше) — мёртвый плеер не будим, его rAF-цикл ожил бы без канваса
    return () => { if (host.instanceRef.current === inst) inst?.resume(); };
  }, [paused, phase]);
  useEffect(() => { if (phase === 'result' || phase === 'story') setPaused(false); }, [phase]);

  return (
    // канвас уровня заливает весь вьюпорт; тёмный фон виден только как letterbox
    // при несовпадении пропорций. Шапка и сообщения — оверлеи поверх канваса.
    <main
      data-testid="screen-play"
      style={{
        position: 'fixed', inset: 0, overflow: 'hidden',
        background: '#1E1A14', color: '#F4ECDD',
      }}
    >
      <div ref={host.mountRef} data-testid="play-mount" style={{ position: 'absolute', inset: 0 }} />
      <p className="play-hud">
        <button
          type="button"
          className="play-hud-btn"
          data-testid="play-settings"
          aria-label="Пауза и настройки"
          title="Пауза и настройки"
          aria-expanded={paused}
          onClick={() => setPaused(true)}
        >
          <img src="/assets/meta/icon-settings.png" alt="" />
        </button>
      </p>
      {paused && (
        <PausePanel
          canRestart={phase === 'playing' || phase === 'error'}
          onResume={() => setPaused(false)}
          onRestart={() => { setPaused(false); host.restart(); }}
          onExit={() => navigate('/map')}
        />
      )}
      {phase === 'loading' && (
        <p data-testid="play-loading" className="play-note">Загрузка уровня…</p>
      )}
      {phase === 'story' && storyQueue && storyQueue.clips[0] && (
        <StoryCinematic
          key={storyQueue.clips[0].ep}
          url={storyQueue.clips[0].url}
          volume={prefs.music}
          onDone={() => finishStory(true)}
          onFail={() => finishStory(false)}
        />
      )}
      {phase === 'locked' && (
        <output data-testid="play-locked" className="play-note">
          Уровень пока закрыт — сначала пройди предыдущий.{' '}
          <Link to="/map">На карту</Link>
        </output>
      )}
      {phase === 'paywall' && (
        /* серверный гейт /config: payment_required — уровень из платной части,
           401 — гость на paid-уровне, зовём войти */
        <output data-testid="play-paywall" className="play-note">
          {paywallLogin ? (
            <>
              Уровень из полной версии — нужен вход.{' '}
              <Link to="/login">Войти</Link>{' '}
              <Link to="/map">На карту</Link>
            </>
          ) : (
            <>
              Этот уровень — из полной версии игры.{' '}
              <Link to="/map">На карту</Link>
            </>
          )}
        </output>
      )}
      {phase === 'error' && (
        <output data-testid="play-error" className="play-note err">
          Не получилось: {error}
        </output>
      )}
      {phase === 'result' && result && (
        /* плеер к этому моменту уже доехал наездом камеры до персонажа (WIN_HOLD) —
           попап мягко проявляется сверху */
        <LevelResultPanel result={result} testId="play-result" starsTestId="result-stars"
          actions={
            /* победа с продолжением (кит §03): primary — «Далее», ретрай уходит
               вниз круглой ghost-кнопкой; последний уровень/поражение — «Ещё раз» */
            result.status === 'completed' && nextId ? (
              <>
                <Link
                  to={`/play/${nextId}`}
                  className="btn btn-primary btn-lg"
                  data-testid="play-next"
                >
                  Далее
                </Link>
                <div className="result-actions-row">
                  <button
                    className="btn btn-ghost btn-round"
                    data-testid="play-again"
                    aria-label="Ещё раз"
                    title="Ещё раз"
                    onClick={host.restart}
                  >
                    <svg
                      viewBox="0 0 24 24" aria-hidden="true"
                      style={{ width: 22, height: 22, fill: 'none', stroke: 'currentColor', strokeWidth: 2.3, strokeLinecap: 'round', strokeLinejoin: 'round' }}
                    >
                      <path d="M20 12a8 8 0 1 1-2.3-5.6" />
                      <polyline points="20 4 20 9 15 9" />
                    </svg>
                  </button>
                  <Link to="/map" className="btn btn-round" aria-label="К карте" title="К карте">
                    <img src="/assets/meta/icon-map.png" alt="" style={{ width: 26, height: 26, objectFit: 'contain' }} />
                  </Link>
                </div>
              </>
            ) : (
              <>
                <button
                  className="btn btn-primary btn-lg"
                  data-testid="play-again"
                  onClick={host.restart}
                >
                  Ещё раз
                </button>
                <div className="result-actions-row">
                  <Link to="/map" className="btn btn-round" aria-label="К карте" title="К карте">
                    <img src="/assets/meta/icon-map.png" alt="" style={{ width: 26, height: 26, objectFit: 'contain' }} />
                  </Link>
                </div>
              </>
            )
          }
        />
      )}
    </main>
  );
}
